/**
 * PATH: packages/ui/src/kanban/components/card-detail/entries/CardEntriesSection.tsx
 * FILE_ID: UI-KANBAN-ENTRIES-SECTION-001
 * PURPOSE: The drawer's "Entries" section — a quick excel-table-like
 *          editor (teammate | action | what | date | amount) whose
 *          rows mint REAL dependency sub-cards under the card, plus
 *          the live rows with done/dismiss/reorder/expand.
 * WHY: ARC-3 (U17), operator 2026-07-02: "underneath the cards where
 *      the notes is, allow quick excel-table-like entry … creating
 *      essentially 'new' cards / sub-card-like structure underneath
 *      which we can treat as other cards but act as ideal dependencies
 *      and chains: 'this needs to happen next'."
 * FLOW:
 *   card → useCardEntries → live rows (EntryLiveRow) + draft rows
 *     → Save → POST /cards/<id>/entries/bulk/ → sub-cards on the tree
 * RELATED:
 *   - useCardEntries.ts / entryDraft.ts / EntryLiveRow.tsx
 *   - CardTabContent.tsx: mounts this under the Description (notes)
 */

import { useCallback, useEffect, useMemo, useRef, type ClipboardEvent } from 'react'
import { type useQueryClient } from '@tanstack/react-query'
import { CornerDownRight, Download, Link2, Loader2, Plus, Sparkles, Table2 } from 'lucide-react'
import type { KanbanApi, KanbanMember } from '../../../api'
import type { Card } from '../../../types'
import { consumeEntriesFocus } from './entriesFocusSignal'
import { entriesToCsv, isTabularPaste, parseTsvToDrafts } from './entryClipboard'
import { draftIsBlank } from './entryDraft'
import { EntryLiveRow } from './EntryLiveRow'
import { useCardEntries } from './useCardEntries'
import { useEntriesAiFill } from './useEntriesAiFill'

const CELL = 'px-1.5 py-1'
const INPUT =
  'w-full rounded border border-neutral-200 bg-white px-1.5 py-0.5 text-[12px] '
  + 'text-neutral-800 placeholder:text-neutral-400 focus:border-sky-400 focus:outline-none '
  + 'dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100'

export function CardEntriesSection({
  card,
  api,
  qc,
  boardId,
  members,
  onOpenCardId,
}: {
  card: Card
  api: KanbanApi
  qc: ReturnType<typeof useQueryClient>
  boardId?: string
  members?: KanbanMember[]
  onOpenCardId?: (cardId: string) => void | Promise<void>
}) {
  const {
    entries, entriesLoading, drafts, updateDraft, addDraft, removeDraft,
    setDrafts, saveDrafts, saving, error, setDone, dismiss, reorder, mutating,
  } = useCardEntries({ card, api, qc, boardId })

  // "AI draft rows" — confirm-to-apply: rows land as editable drafts,
  // × is the ✗, "Save as sub-cards" is the ✓ (see useEntriesAiFill).
  const { aiFill, aiBusy, aiHint } = useEntriesAiFill({
    card, api, entries, members, drafts, setDrafts,
  })

  // Card-face rail → Entries deep-link: when the drawer was opened via
  // the tile's Entries button, scroll this section into view on mount.
  const sectionRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (!consumeEntriesFocus(card.id)) return
    const t = setTimeout(
      () => sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      80,
    )
    return () => clearTimeout(t)
  }, [card.id])

  const memberById = useMemo(() => {
    const map = new Map<number, KanbanMember>()
    for (const m of members ?? []) map.set(m.id, m)
    return map
  }, [members])

  const doneCount = entries.filter((e) => e.status === 'DONE').length

  // ARC-3 §4.3 — paste 4 rows from Excel → 4 draft rows. Fires on any
  // input in the draft area: a multi-cell (tab/newline) paste expands
  // into rows, replacing untouched drafts and appending after edited
  // ones; a plain single-cell paste falls through to the input.
  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLTableSectionElement>) => {
      const text = e.clipboardData?.getData('text/plain') ?? ''
      if (!isTabularPaste(text)) return
      const pasted = parseTsvToDrafts(text, members ?? [])
      if (pasted.length === 0) return
      e.preventDefault()
      setDrafts([...drafts.filter((d) => !draftIsBlank(d)), ...pasted])
    },
    [drafts, members, setDrafts],
  )

  const exportCsv = useCallback(() => {
    const blob = new Blob([entriesToCsv(entries, memberById)], {
      type: 'text/csv;charset=utf-8',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `entries-${(card.title || card.id).slice(0, 40)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }, [card.id, card.title, entries, memberById])

  return (
    <section data-testid="card-entries-section" ref={sectionRef}>
      <div className="mb-1 flex items-center justify-between">
        <h3 className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-neutral-500 dark:text-neutral-400">
          <Table2 className="h-3 w-3" /> Entries
          {entries.length > 0 && (
            <span className="ml-1 rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] font-normal text-neutral-600 dark:bg-neutral-900 dark:text-neutral-300">
              {entries.length} · {doneCount} done
            </span>
          )}
        </h3>
      </div>
      <p className="mb-1.5 text-[10.5px] text-neutral-400">
        Each row is a real sub-card — assign a teammate, chain it after the
        previous row, open it like any other card. Paste rows straight from
        Excel.
      </p>

      <div className="overflow-x-auto rounded-md border border-neutral-200 dark:border-neutral-800">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-neutral-200 bg-neutral-50 text-[10px] uppercase tracking-wide text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900/60 dark:text-neutral-400">
              <th className={CELL} aria-label="Done" />
              <th className={CELL}>Teammate</th>
              <th className={CELL}>Action — what</th>
              <th className={CELL}>Date</th>
              <th className={`${CELL} text-right`}>Amount</th>
              <th className={CELL} aria-label="Row actions" />
            </tr>
          </thead>
          <tbody onPaste={handlePaste} data-testid="entries-tbody">
            {entriesLoading && (
              <tr>
                <td colSpan={6} className="px-2 py-2 text-[11px] text-neutral-500">
                  Loading entries…
                </td>
              </tr>
            )}
            {entries.map((entry, i) => (
              <EntryLiveRow
                key={entry.id}
                entry={entry}
                index={i}
                count={entries.length}
                memberById={memberById}
                busy={mutating}
                onSetDone={setDone}
                onDismiss={dismiss}
                onReorder={reorder}
                onOpen={onOpenCardId}
              />
            ))}
            {drafts.map((row, i) => (
              <tr
                key={row.key}
                data-testid={`entry-draft-${row.key}`}
                className="border-b border-dashed border-neutral-100 last:border-0 dark:border-neutral-800/60"
              >
                <td className={`${CELL} align-middle`}>
                  <button
                    type="button"
                    onClick={() => updateDraft(row.key, { followsPrev: !row.followsPrev })}
                    disabled={i === 0 && entries.length === 0}
                    aria-pressed={row.followsPrev}
                    aria-label="Chain after previous row"
                    title={row.followsPrev
                      ? 'Chained: happens after the row above'
                      : 'Click to chain this after the row above ("this needs to happen next")'}
                    className={[
                      'rounded p-0.5',
                      row.followsPrev
                        ? 'bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300'
                        : 'text-neutral-300 hover:text-neutral-500 dark:text-neutral-600',
                    ].join(' ')}
                  >
                    <Link2 className="h-3 w-3" />
                  </button>
                </td>
                <td className={CELL}>
                  <select
                    value={row.teammate_id ?? ''}
                    onChange={(e) =>
                      updateDraft(row.key, {
                        teammate_id: e.target.value ? Number(e.target.value) : null,
                      })}
                    aria-label="Teammate"
                    className={INPUT}
                  >
                    <option value="">—</option>
                    {(members ?? []).map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </td>
                <td className={CELL}>
                  <div className="flex gap-1">
                    <input
                      value={row.action}
                      onChange={(e) => updateDraft(row.key, { action: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); addDraft() }
                      }}
                      placeholder="Action (e.g. follow up quote)"
                      aria-label="Action"
                      className={INPUT}
                    />
                    <input
                      value={row.what}
                      onChange={(e) => updateDraft(row.key, { what: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); addDraft() }
                      }}
                      placeholder="What / thing"
                      aria-label="What"
                      className={INPUT}
                    />
                  </div>
                </td>
                <td className={CELL}>
                  <input
                    type="date"
                    value={row.date}
                    onChange={(e) => updateDraft(row.key, { date: e.target.value })}
                    aria-label="Date"
                    className={INPUT}
                  />
                </td>
                <td className={CELL}>
                  <input
                    value={row.amountText}
                    onChange={(e) => updateDraft(row.key, { amountText: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); addDraft() }
                    }}
                    placeholder="$"
                    inputMode="decimal"
                    aria-label="Amount"
                    className={`${INPUT} text-right`}
                  />
                </td>
                <td className={`${CELL} text-right`}>
                  <button
                    type="button"
                    onClick={() => removeDraft(row.key)}
                    aria-label="Remove draft row"
                    className="rounded px-1 text-[13px] leading-none text-neutral-300 hover:text-red-500 dark:text-neutral-600"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-1.5 flex items-center gap-2">
        <button
          type="button"
          onClick={addDraft}
          className="inline-flex items-center gap-1 rounded-md border border-neutral-200 px-1.5 py-0.5 text-[11px] text-neutral-600 hover:bg-neutral-100 dark:border-neutral-800 dark:text-zinc-300 dark:hover:bg-zinc-900"
        >
          <Plus className="h-3 w-3" /> Row
        </button>
        <button
          type="button"
          onClick={saveDrafts}
          disabled={saving}
          data-testid="entries-save"
          className="inline-flex items-center gap-1 rounded-md bg-sky-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-sky-700 disabled:opacity-60"
        >
          {saving && <Loader2 className="h-3 w-3 animate-spin" />}
          <CornerDownRight className="h-3 w-3" /> Save as sub-cards
        </button>
        {api.suggestCardActions && (
          <button
            type="button"
            onClick={() => void aiFill()}
            disabled={aiBusy}
            data-testid="entries-ai-fill"
            title="AI drafts the standard next steps from the card's context — you edit and save"
            className="inline-flex items-center gap-1 rounded-md border border-violet-200 px-1.5 py-0.5 text-[11px] text-violet-700 hover:bg-violet-50 disabled:opacity-60 dark:border-violet-900/60 dark:text-violet-300 dark:hover:bg-violet-950/40"
          >
            {aiBusy
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <Sparkles className="h-3 w-3" />}
            AI draft
          </button>
        )}
        {entries.length > 0 && (
          <button
            type="button"
            onClick={exportCsv}
            data-testid="entries-export-csv"
            title="Download this card's entries as a CSV"
            className="inline-flex items-center gap-1 rounded-md border border-neutral-200 px-1.5 py-0.5 text-[11px] text-neutral-600 hover:bg-neutral-100 dark:border-neutral-800 dark:text-zinc-300 dark:hover:bg-zinc-900"
          >
            <Download className="h-3 w-3" /> CSV
          </button>
        )}
        {error && (
          <p className="text-[11px] text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        )}
      </div>
      {aiHint && (
        <p
          data-testid="entries-ai-hint"
          className="mt-1 text-[10.5px] text-violet-600 dark:text-violet-400"
        >
          {aiHint}
        </p>
      )}
    </section>
  )
}
